import { useCallback, useRef, useState } from 'react';

export function useInViewport<T extends HTMLElement = any>({
  enabled = true,
  rootMargin,
}: { enabled?: boolean; rootMargin?: string } = {}) {
  const observer = useRef<IntersectionObserver | null>(null);
  const [inViewport, setInViewport] = useState(false);

  const ref = useCallback(
    (node: T | null) => {
      if (!enabled) {
        observer.current?.disconnect();
        observer.current = null;
        return;
      }

      if (typeof IntersectionObserver === 'undefined') return;

      // Drop the observer rather than only disconnecting it: it is constructed with `rootMargin`,
      // so a retained instance would silently keep observing with the previous margin.
      observer.current?.disconnect();
      observer.current = null;

      if (!node) {
        setInViewport(false);
        return;
      }

      observer.current = new IntersectionObserver(
        (entries) => {
          const nextInViewport = entries.some((entry) => entry.isIntersecting);
          setInViewport((current) =>
            current === nextInViewport ? current : nextInViewport
          );
        },
        { rootMargin }
      );
      observer.current.observe(node);
    },
    [enabled, rootMargin]
  );

  return { ref, inViewport: enabled ? inViewport : true };
}
